import {
  Button,
  Card,
  CardContent,
  cn,
  Flex,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDisplayName } from '@/shared/utils';
import { useIsOverflowing } from '@/shared/hooks';
import type { IOutputCardWrapperProps } from '../types';

// Default config object outside component to prevent re-creation
const DEFAULT_CONFIG: IOutputCardWrapperProps['config'] = {
  disableScroll: false,
};

/**
 * Renders the output label and, only when it is truncated by the card's CSS
 * ellipsis, reveals the full label in a tooltip on hover.
 *
 * The trigger stays mounted either way and only `TooltipContent` is
 * conditional, so React never remounts the subtree — see {@link ValueContent}.
 */
function LabelText({ label, testId }: { label: string; testId?: string }) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const isOverflowing = useIsOverflowing(labelRef);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span ref={labelRef} className="min-w-0 truncate" data-testid={testId}>
          <Text
            as="span"
            level="small"
            color="muted"
            className="tracking-[0.12px]"
          >
            {label}
          </Text>
        </span>
      </TooltipTrigger>
      {isOverflowing && <TooltipContent>{label}</TooltipContent>}
    </Tooltip>
  );
}

/**
 * Renders the output value and, when the value is visually clipped by the card
 * (CSS ellipsis in the horizontal layout, or the fixed height in the vertical
 * layout), reveals the full value in a tooltip on hover.
 *
 * Only `TooltipContent` is conditional. Swapping the wrapper element once the
 * value overflows would remount the subtree and restart a file preview's load
 * pipeline, which flickered the run-history popover (KOG-11897).
 *
 * `disableTooltip` opts rich previews out entirely, so hovering doesn't mount a
 * second copy of the renderer in the popper. Callers should set it for every
 * non-primitive value: re-rendering a dict/table/array/file renderer inside a
 * max-w-xs popover reads as a tooltip nested in a tooltip, and those cards
 * already expose the full value via the expand button.
 */
function ValueContent({
  children,
  className,
  onClick,
  testId,
  disableTooltip = false,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  testId?: string;
  disableTooltip?: boolean;
}) {
  const valueRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsOverflowing(valueRef);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          ref={valueRef}
          data-testid={testId}
          className={className}
          onClick={onClick}
        >
          {children}
        </div>
      </TooltipTrigger>
      {isOverflowing && !disableTooltip && (
        <TooltipContent className="max-w-xs break-words">
          {children}
        </TooltipContent>
      )}
    </Tooltip>
  );
}

/**
 * Base output card component that displays output data with responsive layout.
 * - Uses horizontal layout for compact content that fits on one line
 * - Uses vertical layout for larger content
 * - Displays an icon representing the data type
 *
 * @param props - Component props
 * @returns Output card with header and content area
 */
export function OutputCardWrapper({
  name,
  children,
  horizontal = false,
  onExpand,
  onDownload,
  iconType,
  displayName,
  config = DEFAULT_CONFIG,
  scrollAreaRef,
  headerActions,
  titleAdornment,
  headerProps,
}: IOutputCardWrapperProps) {
  const { t } = useTranslation('outputs');
  const label = displayName ?? formatDisplayName(name);
  const { className: headerClassName, ...headerEventProps } = headerProps ?? {};

  if (horizontal) {
    return (
      <Card className="min-w-0 max-w-full p-2 rounded-lg overflow-hidden group shadow-custom">
        <CardContent className="p-0 overflow-hidden">
          <Flex
            align="center"
            justify="between"
            gap={2}
            className="min-w-0 w-full"
          >
            <Flex
              align="center"
              gap={1.5}
              className="p-0.5 min-w-0 max-w-[100px] shrink-0"
            >
              <Icon
                size="sm"
                type={iconType}
                className="text-muted-foreground shrink-0"
              />
              <LabelText label={label} />
            </Flex>
            <Flex
              align="center"
              justify="end"
              gap={0.5}
              className="flex-1 min-w-0"
            >
              <ValueContent
                testId="output-card-value"
                className="min-w-0 text-sm font-medium text-foreground truncate text-right rounded-xs"
                disableTooltip={config?.disableValueTooltip}
              >
                {children}
              </ValueContent>
            </Flex>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        'min-w-0 max-w-full overflow-hidden p-2 pt-1 rounded-lg group shadow-custom',
        // Aspect-ratio renderers size the card; other outputs keep a fixed height.
        config?.fitContent ? 'h-auto w-full' : 'h-[200px] max-h-[200px]',
      )}
    >
      <CardContent className="p-0 h-full flex flex-col overflow-hidden rounded-xs">
        <Flex
          {...headerEventProps}
          align="center"
          justify="between"
          gap={2}
          className={cn('shrink-0 rounded-xs mb-1', headerClassName)}
        >
          <Flex align="center" gap={1.5} className="min-w-0">
            <Icon
              size="sm"
              type={iconType}
              className="text-muted-foreground shrink-0"
            />
            <LabelText label={label} testId="output-card-title" />
            {titleAdornment}
          </Flex>
          {(onDownload || onExpand || headerActions) && (
            <Flex align="center" gap={0.5} className="shrink-0">
              {onDownload && (
                <Button
                  size="icon-sm"
                  onClick={onDownload}
                  variant="ghost"
                  aria-label={t('outputs.actions.download')}
                  className="border-none focus-visible:ring-0 text-muted-foreground"
                >
                  <Icon type="Download" size="xs" />
                </Button>
              )}
              {onExpand && (
                <Button
                  size="icon-sm"
                  onClick={onExpand}
                  variant="ghost"
                  aria-label={t('outputs.actions.expand')}
                  className="border-none focus-visible:ring-0 text-muted-foreground"
                >
                  <Icon type="Maximize" size="xs" />
                </Button>
              )}
              {headerActions}
            </Flex>
          )}
        </Flex>
        <div
          ref={scrollAreaRef}
          className={cn(
            'flex-1 min-h-0',
            // Scroll the value area so content taller than the fixed-height
            // card stays reachable; IDP cards opt out (disableScroll) because
            // their renderer manages its own scrolling. scrollbar-gutter keeps
            // the width stable so PDF previews don't oscillate (KOG-11897).
            config?.disableScroll || config?.fitContent
              ? 'overflow-hidden'
              : 'overflow-auto [scrollbar-gutter:stable]',
          )}
        >
          <ValueContent
            testId="output-card-value"
            className={cn(
              'text-sm font-medium text-foreground leading-4 break-words',
              onExpand && 'cursor-pointer',
            )}
            onClick={onExpand}
            disableTooltip={config?.disableValueTooltip}
          >
            {children}
          </ValueContent>
        </div>
      </CardContent>
    </Card>
  );
}
